import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Anchor, Button, Card, Group, SegmentedControl, Stack, Text, TextInput } from "@mantine/core";
import { Link } from "react-router-dom";
import { AlertTriangle, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { tenantApi, type BillingDetailsError, type OrgNumberVerifyError } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth";
import { supportLink } from "@/components/network/shared";
import { PENDING_PLAN_CHANGE_KEY } from "@/components/network/plan-card";
import { NETWORK_PLAN_QUERY_KEY } from "@/hooks/use-network-queries";
import { EinvoiceAddressField } from "@/components/einvoice-address-field";
import { billingErrorField, einvoiceHelp } from "@/lib/einvoice";

/** Billing details (#313) — invoice delivery, reference and address, required
 *  once before the first self-service plan change. Email and e-invoice are
 *  peers (a segmented control, not a checkbox afterthought): plenty of Swedish
 *  tenants will not accept a PDF by email.
 *
 *  The organisation number lives on the TENANT record, one source of truth:
 *  when staff already set it, it is shown read-only here; when it is missing,
 *  this form collects it and the backend verifies it against the company
 *  register — ONLY a match saves. A mismatch or a failed lookup BLOCKS the
 *  save and routes to IT with the context prefilled; a warning the member can
 *  click past is not a control. The copy never names the lookup vendor. */
export function BillingDetailsCard({
  onSaved,
  onResumePlanChange,
}: {
  /** Called after a successful save (the Company setup guide re-checks its step). */
  onSaved?: () => void;
  /** Where to go when a plan change was parked waiting for these details. The
   *  card itself doesn't navigate, so it renders without a router hook. */
  onResumePlanChange?: () => void;
} = {}) {
  const queryClient = useQueryClient();
  const tenantName = useAuthStore((s) => s.user?.tenant_name);

  const { data, isLoading } = useQuery({
    queryKey: ["billing-details"],
    queryFn: () => tenantApi.getBillingDetails().then((r) => r.data),
    retry: false,
  });

  const [method, setMethod] = useState<"email" | "einvoice">("email");
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const [invoiceEmailCc, setInvoiceEmailCc] = useState("");
  const [invoiceName, setInvoiceName] = useState("");
  const [einvoiceAddress, setEinvoiceAddress] = useState("");
  /** The backend's refusal of the e-invoice address on the last save, shown under the field. */
  const [einvoiceError, setEinvoiceError] = useState<string | null>(null);
  const [reference, setReference] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [postcode, setPostcode] = useState("");
  const [city, setCity] = useState("");
  const [orgNumber, setOrgNumber] = useState("");
  /** Set from a 409 on save: the typed organisation number could not be
   *  verified. While present the save stays blocked — editing the number is
   *  the only way out, besides the IT handoff. */
  const [orgBlock, setOrgBlock] = useState<{ kind: "mismatch" | "unavailable"; registeredName?: string } | null>(null);

  useEffect(() => {
    if (!data) return;
    setMethod(data.method);
    setInvoiceEmail(data.invoice_email);
    setInvoiceEmailCc(data.invoice_email_cc ?? "");
    setInvoiceName(data.invoice_name ?? "");
    setEinvoiceAddress(data.einvoice_address);
    setReference(data.reference);
    setAddress1(data.address_line1);
    setAddress2(data.address_line2);
    setPostcode(data.postcode);
    setCity(data.city);
  }, [data]);

  // Non-null means staff set it on the tenant — read-only here. Null means
  // the form collects it (the smaller, riskier case the verification governs).
  const collectOrg = !!data && data.organisation_number == null;

  const saveMutation = useMutation({
    // Verification is its OWN endpoint, called before the billing PUT: the
    // org number is verified state on the tenant record, not another billing
    // field, and a failed verification must block the whole save — so a
    // verify refusal is re-thrown tagged and the PUT never runs.
    mutationFn: async () => {
      if (collectOrg && orgNumber.trim()) {
        try {
          await tenantApi.verifyOrganisationNumber(orgNumber.trim());
        } catch (err) {
          throw Object.assign(new Error("org_verify_failed"), { orgVerify: err });
        }
      }
      return tenantApi.updateBillingDetails({
        method,
        invoice_email: invoiceEmail.trim(),
        // Always sent: the PUT replaces the row, so leaving it out would clear it. The accounting
        // system only uses it for emailed invoices.
        invoice_email_cc: invoiceEmailCc.trim(),
        invoice_name: invoiceName.trim(),
        // The address field is hidden in email mode — never resubmit a stored value
        // the user can't see (a legacy invalid one would 400 every save).
        einvoice_address: method === "einvoice" ? einvoiceAddress.trim() : "",
        reference: reference.trim(),
        address_line1: address1.trim(),
        address_line2: address2.trim(),
        postcode: postcode.trim(),
        city: city.trim(),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing-details"] });
      // billing_details_complete lives on the plan payload — refetch it so a
      // parked plan change sees the gate open.
      queryClient.invalidateQueries({ queryKey: NETWORK_PLAN_QUERY_KEY });
      if (sessionStorage.getItem(PENDING_PLAN_CHANGE_KEY) && onResumePlanChange) {
        toast.success("Billing details saved — let's finish your plan change");
        onResumePlanChange();
      } else {
        toast.success("Billing details saved");
      }
      onSaved?.();
    },
    onError: (err: unknown) => {
      const verifyErr = (err as { orgVerify?: unknown }).orgVerify;
      if (verifyErr) {
        const resp = (verifyErr as { response?: { status?: number; data?: OrgNumberVerifyError } }).response;
        if (resp?.data?.code === "org_number_mismatch") {
          setOrgBlock({ kind: "mismatch", registeredName: resp.data.registered_name ?? undefined });
          return;
        }
        // Unknown number (404), register unreachable (502), already
        // set/in use (other 409s) — all block the same way: a human sorts
        // out the exception, never a checkbox the member clicks past.
        setOrgBlock({ kind: "unavailable" });
        return;
      }
      const body = (err as { response?: { status?: number; data?: BillingDetailsError } })?.response;
      const detail = typeof body?.data?.detail === "string" ? body.data.detail : "";
      if (body?.status === 400 && detail && method === "einvoice" && billingErrorField(detail).field === "einvoice_address") {
        setEinvoiceError(detail);
        return;
      }
      toast.error(detail || "Failed to save billing details");
    },
  });

  /** Every verification exit lands in the same prefilled IT conversation,
   *  carrying what we hold and what the lookup returned — the member never
   *  retypes context we already have. */
  const orgHandoffLink = () => {
    const lines = [
      "Hi! We tried to add our billing details, but the organisation number needs checking.",
      "",
      `Company: ${tenantName ?? ""}`,
      `Entered: ${orgNumber.trim()}`,
    ];
    if (orgBlock?.registeredName) lines.push(`Lookup returned: ${orgBlock.registeredName}`);
    return supportLink("Billing details — organisation number needs checking", lines.join("\n"));
  };

  const foreignCompanyLink = supportLink(
    "Billing details — no Swedish organisation number",
    `Hi! We don't have a Swedish organisation number, so we can't complete our billing details in the portal. Company: ${tenantName ?? ""}. Could you set this up with us?`,
  );

  const missingRequired =
    (method === "email" ? !invoiceEmail.trim() : !einvoiceAddress.trim()) ||
    !reference.trim() ||
    !address1.trim() ||
    !postcode.trim() ||
    !city.trim() ||
    (collectOrg && !orgNumber.trim());
  const einvoiceProblem =
    method === "einvoice" && !!einvoiceHelp(einvoiceAddress, data?.organisation_number ?? orgNumber).problem;

  return (
    <Card withBorder padding="lg">
      <Group gap="xs" mb={4}>
        <CreditCard size={20} style={{ color: "var(--mantine-primary-color-filled)" }} />
        <Text fw={500}>Billing details</Text>
      </Group>
      <Text fz="xs" c="dimmed" mb="md">
        Needed once, before your first plan change.
      </Text>

      {isLoading ? (
        <Text fz="sm" c="dimmed">Loading…</Text>
      ) : !data ? (
        <Text fz="sm" c="dimmed">Billing details aren't available right now. Please try again.</Text>
      ) : (
        <Stack gap="sm">
          <SegmentedControl
            fullWidth
            value={method}
            onChange={(value) => setMethod(value as "email" | "einvoice")}
            data={[
              { value: "email", label: "Email invoice" },
              { value: "einvoice", label: "E-invoice" },
            ]}
          />

          {method === "email" ? (
            <>
              <TextInput
                label="Invoice email"
                required
                type="email"
                placeholder="faktura@example.se"
                value={invoiceEmail}
                onChange={(e) => setInvoiceEmail(e.currentTarget.value)}
              />
              <TextInput
                label="Copy to (optional)"
                description="Another address that gets a copy of every invoice email."
                type="email"
                placeholder="ekonomi@example.se"
                value={invoiceEmailCc}
                onChange={(e) => setInvoiceEmailCc(e.currentTarget.value)}
              />
            </>
          ) : (
            <EinvoiceAddressField
              value={einvoiceAddress}
              onChange={(v) => {
                setEinvoiceAddress(v);
                setEinvoiceError(null);
              }}
              organisationNumber={data.organisation_number ?? (orgNumber.trim() || null)}
              serverError={einvoiceError}
              savedAddress={data.method === "einvoice" ? data.einvoice_address : ""}
              peppolRegistered={data.peppol_registered ?? null}
              manualSetup={data.einvoice_note === "einvoice_manual_setup"}
              // The lookup reads the organisation number on record — not a typed, unverified one.
              lookup={data.organisation_number ? () => tenantApi.peppolLookup().then((r) => r.data) : undefined}
            />
          )}

          <TextInput
            label="Name on invoices"
            description="Your company's legal name as it should appear on invoices. Leave empty to use your company name."
            value={invoiceName}
            onChange={(e) => setInvoiceName(e.currentTarget.value)}
          />
          <TextInput
            label="Your reference"
            description="The marking your invoice department needs to route the invoice."
            required
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
          />
          <TextInput
            label="Address"
            required
            value={address1}
            onChange={(e) => setAddress1(e.currentTarget.value)}
          />
          <TextInput
            label="Address line 2"
            value={address2}
            onChange={(e) => setAddress2(e.currentTarget.value)}
          />
          <Group grow>
            <TextInput
              label="Postcode"
              required
              value={postcode}
              onChange={(e) => setPostcode(e.currentTarget.value)}
            />
            <TextInput
              label="City"
              required
              value={city}
              onChange={(e) => setCity(e.currentTarget.value)}
            />
          </Group>

          {collectOrg ? (
            <Stack gap={4}>
              <TextInput
                label="Organisation number"
                required
                placeholder="556677-8899"
                value={orgNumber}
                onChange={(e) => {
                  setOrgNumber(e.currentTarget.value);
                  // A new number is a new question — the block belongs to the
                  // one that failed.
                  setOrgBlock(null);
                }}
                error={orgBlock ? " " : undefined}
              />
              <Text fz="xs" c="dimmed">
                Saved to your company record, not just this form.
              </Text>
              {orgBlock?.kind === "mismatch" && (
                <Alert color="yellow" icon={<AlertTriangle size={16} />} p="sm">
                  <Text fz="sm">
                    This number belongs to{" "}
                    <Text component="span" fz="sm" fw={600}>{orgBlock.registeredName || "another company"}</Text>,
                    but your company is recorded as{" "}
                    <Text component="span" fz="sm" fw={600}>{tenantName}</Text>. We can't save it from
                    here — <Anchor component={Link} to={orgHandoffLink()} fz="sm">let IT sort it out →</Anchor>
                  </Text>
                </Alert>
              )}
              {orgBlock?.kind === "unavailable" && (
                <Alert color="yellow" icon={<AlertTriangle size={16} />} p="sm">
                  <Text fz="sm">
                    We can't check this number right now. Nothing is wrong on your side —{" "}
                    <Anchor component={Link} to={orgHandoffLink()} fz="sm">IT can finish this for you →</Anchor>
                  </Text>
                </Alert>
              )}
              <Text fz="xs" c="dimmed">
                No Swedish organisation number?{" "}
                <Anchor component={Link} to={foreignCompanyLink} fz="xs">Contact IT support →</Anchor>
              </Text>
            </Stack>
          ) : (
            <Text fz="xs" c="dimmed">
              Organisation number <Text component="span" fz="xs" fw={600}>{data.organisation_number}</Text> is
              taken from your company record and is not edited here.
            </Text>
          )}

          <Group justify="flex-end" mt="xs">
            <Button
              onClick={() => saveMutation.mutate()}
              loading={saveMutation.isPending}
              disabled={missingRequired || !!orgBlock || einvoiceProblem}
            >
              Save billing details
            </Button>
          </Group>
        </Stack>
      )}
    </Card>
  );
}

export default BillingDetailsCard;
